// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { formatBaht } from '@/lib/money'
import { User, Phone, Mail, CreditCard, MapPin, Edit, Trash2, FileText, Calendar, Plus, Receipt } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, SetupGuide, Skeleton, ConfirmDialog } from '@/components/ui'
import { useCustomer, useDeleteCustomer } from '@/hooks/useCustomers'
import { useCustomerLeases } from '@/hooks/useLeases'
import { useInvoices } from '@/hooks/useInvoices'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { ExploreCoachBanner } from '@/components/onboarding/ExploreCoachBanner'
import { SupportSessionHidden } from '@/components/SupportSessionHidden'
import { useSetupScopeSession } from '@/hooks/useSupportSession'
import { hiddenInSession, maskedInSession } from '@/lib/sessionRedaction'

export function CustomerDetailPage() {
  // EP-160: a setup-scope support session may not create tenant data.
  const setupScope = useSetupScopeSession()
  const { t } = useTranslation('customers')
  const { t: tc } = useTranslation('common')
  const { t: tl } = useTranslation('leases')
  const { t: ti } = useTranslation('invoices')
  const { id } = useParams<{ id: string }>()
  const customerId = Number(id)
  const navigate = useNavigate()
  const formatDate = useDateFormatter()

  const { data: customer, isLoading, isFetching } = useCustomer(customerId)
  const isExternal = customer?.status === 'external' || customer?.source === 'external'

  // Only fetch leases for non-external customers
  const { data: leases, isLoading: isLoadingLeases } = useCustomerLeases(customerId)

  // Only fetch invoices for external customers.
  // EP-97: allPeriods so this customer's full invoice history shows (not just
  // the latest billing period the list endpoint now defaults to).
  const { data: invoicesData, isLoading: isLoadingInvoices } = useInvoices(
    1,
    undefined,
    undefined,
    undefined,
    undefined,
    isExternal ? customerId : undefined,
    undefined,
    undefined,
    { allPeriods: true }
  )
  const invoices = invoicesData?.data ?? []

  const deleteCustomer = useDeleteCustomer()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const translateError = useApiErrorTranslator('customers')
  const addToast = useNotificationStore((s) => s.addToast)

  const handleDelete = async () => {
    try {
      await deleteCustomer.mutateAsync(customerId)
      navigate('/customers')
    } catch (err) {
      // WEB-H6: surface the failure (e.g. 422 customer has an active lease)
      // instead of silently staying on the page; don't navigate away.
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">{t('status.active')}</Badge>
      case 'alumni':
        return <Badge variant="ghost">{t('status.alumni')}</Badge>
      case 'prospect':
        return <Badge variant="warning">{t('status.prospect')}</Badge>
      case 'external':
        return <Badge variant="info">{t('status.external')}</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getLeaseBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge>{tl('status.draft')}</Badge>
      case 'pending_signatures':
        return <Badge variant="warning">{tl('status.pending_signatures')}</Badge>
      case 'pending_start':
        return <Badge variant="info">{tl('status.pending_start')}</Badge>
      case 'active':
        return <Badge variant="success">{tl('status.active')}</Badge>
      case 'pending':
        return <Badge variant="warning">{tl('status.pending')}</Badge>
      case 'ended':
        return <Badge variant="ghost">{tl('status.ended')}</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getInvoiceStatusBadge = (invoice: { paid: boolean; overdue: boolean }) => {
    if (invoice.paid) {
      return <Badge variant="success">{ti('status.paid')}</Badge>
    }
    if (invoice.overdue) {
      return <Badge variant="error">{ti('status.overdue')}</Badge>
    }
    return <Badge variant="warning">{ti('status.unpaid')}</Badge>
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-32" />
          </Card>
        </div>
    )
  }

  if (!customer) {
    return (
        <Card>
          <EmptyState
            title={t('messages.not_found')}
            action={<Button onClick={() => navigate('/customers')}>{tc('actions.back')}</Button>}
          />
        </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <ExploreCoachBanner pageKey="tenant" />
        <Breadcrumbs
          items={[{ label: t('title'), href: '/customers' }, { label: customer.name }]}
        />

        <PageHeader
          title={customer.name}
          description={customer.phone ?? undefined}
          isRefreshing={isFetching && !isLoading}
          primaryAction={{
            key: 'edit',
            label: tc('actions.edit'),
            icon: Edit,
            to: `/customers/${customerId}/edit`,
          }}
          overflowActions={[
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              destructive: true,
              onClick: () => setConfirmDelete(true),
            },
          ]}
        />

        {/* Customer Info */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* min-w-0: grid items default to min-width:auto; without it wide
              content inflates the card past the viewport on phones. */}
          <Card className="lg:col-span-2 min-w-0">
            <h2 className="text-lg font-semibold mb-4">{t('labels.customer_info')}</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <User className="w-4 h-4" /> {tc('labels.name')}
                </dt>
                <dd className="font-medium break-words">{customer.name}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Phone className="w-4 h-4" /> {t('labels.phone')}
                </dt>
                <dd className="font-medium">{customer.phone ?? t('labels.not_provided')}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Mail className="w-4 h-4" /> {t('labels.email')}
                </dt>
                <dd className="font-medium break-words">{customer.email || t('labels.not_provided')}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <CreditCard className="w-4 h-4" /> {t('labels.id_card_number')}
                </dt>
                <dd className="font-medium break-words">
                  {/* EP-160 S3: a FlowRent support session gets no ID number. */}
                  {hiddenInSession(customer, 'id_card_number')
                    ? <SupportSessionHidden variant="text" testId="customer-id-card-hidden" />
                    : customer.id_card_number || t('labels.not_provided')}
                </dd>
              </div>
              {hiddenInSession(customer, 'tax_id') ? (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> {t('labels.tax_id')}
                  </dt>
                  <dd className="font-medium break-words">
                    <SupportSessionHidden variant="text" testId="customer-tax-id-hidden" />
                  </dd>
                </div>
              ) : customer.tax_id && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> {t('labels.tax_id')}
                  </dt>
                  <dd className="font-medium break-words">{customer.tax_id}</dd>
                </div>
              )}
              {(customer.address || maskedInSession(customer, 'address')) && (
                <div className="col-span-full min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {t('labels.address')}
                  </dt>
                  <dd className="font-medium whitespace-pre-wrap">
                    {customer.address || <SupportSessionHidden variant="text" testId="customer-address-hidden" />}
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold mb-4">{tc('labels.status')}</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">{t('labels.account_status')}</span>
                {getStatusBadge(customer.status)}
              </div>
              {!isExternal && (
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t('labels.lease_status')}</span>
                  {leases && leases.length > 0 ? (
                    getLeaseBadge(leases[0].status)
                  ) : (
                    <Badge variant="ghost">{t('lease.no_lease')}</Badge>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Lease — shown only for non-external customers */}
        {!isExternal && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {leases && leases.length > 0 ? (
                  <Link
                    to={`/leases?customer_id=${customerId}`}
                    className="hover:text-primary transition-colors"
                  >
                    {t('labels.leases_count', { count: leases.length })}
                  </Link>
                ) : (
                  t('lease.title')
                )}
              </h2>
              {(!leases || leases.length === 0) && !setupScope && (
                <Button
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => navigate(`/leases/new?customer_id=${customerId}`)}
                >
                  {t('lease.new_lease')}
                </Button>
              )}
            </div>

            {isLoadingLeases ? (
              <Skeleton className="h-16" />
            ) : !leases || leases.length === 0 ? (
              <SetupGuide
                icon={<FileText className="w-16 h-16" />}
                title={t('lease.setup_guide_title')}
                description={t('lease.setup_guide_description')}
                steps={[
                  { title: t('lease.step_1_title'), description: t('lease.step_1_description') },
                  { title: t('lease.step_2_title'), description: t('lease.step_2_description') },
                  { title: t('lease.step_3_title'), description: t('lease.step_3_description') },
                ]}
                action={
                  setupScope ? undefined : (
                    <Button
                      size="sm"
                      leftIcon={<Plus className="w-4 h-4" />}
                      onClick={() => navigate(`/leases/new?customer_id=${customerId}`)}
                    >
                      {t('lease.create_lease')}
                    </Button>
                  )
                }
              />
            ) : (
              <Link
                to={`/leases/${leases[0].id}`}
                className="block p-4 border border-base-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">{leases[0].lease_code}</h3>
                    <p className="text-sm text-base-content/60 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(leases[0].started_at)} -{' '}
                      {leases[0].ended_at ? formatDate(leases[0].ended_at) : t('lease.ongoing')}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm text-base-content/60">{t('lease.monthly_rent')}</div>
                      <div className="font-semibold">{formatBaht(leases[0].unit?.price ?? 0)}</div>
                    </div>
                    {getLeaseBadge(leases[0].status)}
                  </div>
                </div>
              </Link>
            )}
          </Card>
        )}

        {/* Invoices — shown only for external customers */}
        {isExternal && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                {ti('external_parker_invoice')}
                {invoices.length > 0 && (
                  <span className="text-base-content/60 font-normal">({invoices.length})</span>
                )}
              </h2>
              <Button
                size="sm"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => navigate(`/invoices/new?customer_id=${customerId}`)}
              >
                {ti('actions.create_parking_invoice')}
              </Button>
            </div>

            {isLoadingInvoices ? (
              <Skeleton className="h-16" />
            ) : invoices.length === 0 ? (
              <EmptyState
                title={ti('empty.title')}
                description={ti('empty.description')}
                action={
                  <Button
                    size="sm"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate(`/invoices/new?customer_id=${customerId}`)}
                  >
                    {ti('actions.create_parking_invoice')}
                  </Button>
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="table w-full">
                  <thead>
                    <tr>
                      <th>{ti('labels.invoice_number')}</th>
                      <th>{ti('labels.billed_date')}</th>
                      <th className="text-right">{ti('labels.total')}</th>
                      <th>{tc('labels.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover">
                        <td>
                          <Link
                            to={`/invoices/${invoice.id}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {invoice.number}
                          </Link>
                        </td>
                        <td className="text-sm text-base-content/70">
                          {formatDate(invoice.billed_at)}
                        </td>
                        <td className="text-right font-medium">
                          {formatBaht(invoice.total, { decimals: 2 })}
                        </td>
                        <td>{getInvoiceStatusBadge(invoice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title={t('messages.delete_title')}
        message={t('messages.delete_confirm', { name: customer?.name })}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteCustomer.isPending}
      />
    </>
  )
}
